/**
 * ConcatenatedCoordSplitter.ts
 * Separador de coordenadas UTM concatenadas en documentos PTEL de Andalucía.
 *
 * Detecta y separa pares X/Y que aparecen pegados sin separador,
 * con decimales pegados, o con coma europea pegada.
 *
 * Caso real: Berja (X=50435298504342), documentos escaneados/copiados.
 *
 * @module ConcatenatedCoordSplitter
 * @version 1.0.0
 * @date 2026-02-08
 * @task F2.03
 */

import {
  ANDALUCIA_X_RANGE,
  ANDALUCIA_Y_RANGE,
} from './CoordinateErrorClassification';

// ============================================================================
// TIPOS
// ============================================================================

/** Formato detectado de concatenación. */
export type ConcatFormat =
  | 'Y7X6'             // 13 dígitos: 7Y + 6X
  | 'X6Y7'             // 13 dígitos: 6X + 7Y
  | 'decimal_glued'    // Dos números con punto decimal pegados
  | 'comma_separated'  // Coma como separador de coordenadas (no decimal)
  | 'european_glued'   // Dos números con coma decimal europea pegados
  | 'heuristic_split'; // División heurística para longitudes no estándar

/** Confianza en la separación. */
export type SplitConfidence = 'high' | 'medium' | 'low';

/** Resultado de la separación de coordenadas concatenadas. */
export interface SplitResult {
  readonly split: true;
  readonly originalInput: string;
  readonly x: number;
  readonly y: number;
  readonly format: ConcatFormat;
  readonly confidence: SplitConfidence;
  readonly message: string;
}

// ============================================================================
// CONSTANTES / VALIDACIÓN
// ============================================================================

const X_MIN = ANDALUCIA_X_RANGE.min; // 100_000
const X_MAX = ANDALUCIA_X_RANGE.max; // 620_000
const Y_MIN = ANDALUCIA_Y_RANGE.min; // 4_050_000
const Y_MAX = ANDALUCIA_Y_RANGE.max; // 4_290_000

/** Longitud mínima de entrada para considerar concatenación. */
const MIN_INPUT_LENGTH = 10;

function validX(x: number): boolean {
  return x >= X_MIN && x <= X_MAX;
}
function validY(y: number): boolean {
  return y >= Y_MIN && y <= Y_MAX;
}
function validPair(x: number, y: number): boolean {
  return validX(x) && validY(y);
}

// ============================================================================
// S1: DOS DECIMALES PEGADOS (punto)
// Patrón: "436780.04137500.0", "523456.784137618.90"
// Requiere exactamente 2 puntos en la cadena.
// ============================================================================

function tryDecimalGlued(input: string): SplitResult | null {
  const dots: number[] = [];
  for (let i = 0; i < input.length; i++) {
    if (input[i] === '.') dots.push(i);
  }
  if (dots.length !== 2) return null;

  // Solo dígitos y puntos
  if (!/^[\d.]+$/.test(input)) return null;

  // Intentar cortar entre el primer punto y el segundo
  const firstDot = dots[0];
  const secondDot = dots[1];

  for (let cut = firstDot + 2; cut <= secondDot; cut++) {
    const part1 = input.substring(0, cut);
    const part2 = input.substring(cut);

    // Cada parte debe tener exactamente un punto
    if ((part1.match(/\./g) || []).length !== 1) continue;
    if ((part2.match(/\./g) || []).length !== 1) continue;

    const n1 = parseFloat(part1);
    const n2 = parseFloat(part2);
    if (isNaN(n1) || isNaN(n2)) continue;

    if (validPair(n1, n2)) {
      return mkResult(input, n1, n2, 'decimal_glued', 'high',
        `Decimales pegados: "${input}" → X=${n1}, Y=${n2}`);
    }
    if (validPair(n2, n1)) {
      return mkResult(input, n2, n1, 'decimal_glued', 'high',
        `Decimales pegados: "${input}" → X=${n2}, Y=${n1}`);
    }
  }
  return null;
}

// ============================================================================
// S2: DOS DECIMALES PEGADOS (coma europea)
// Patrón: "436780,04137500,0"
// Requiere exactamente 2 comas y solo dígitos/comas.
// ============================================================================

function tryEuropeanGlued(input: string): SplitResult | null {
  const commas: number[] = [];
  for (let i = 0; i < input.length; i++) {
    if (input[i] === ',') commas.push(i);
  }
  if (commas.length !== 2) return null;
  if (!/^[\d,]+$/.test(input)) return null;

  const firstComma = commas[0];
  const secondComma = commas[1];

  for (let cut = firstComma + 2; cut <= secondComma; cut++) {
    const raw1 = input.substring(0, cut);
    const raw2 = input.substring(cut);

    if ((raw1.match(/,/g) || []).length !== 1) continue;
    if ((raw2.match(/,/g) || []).length !== 1) continue;

    const n1 = parseFloat(raw1.replace(',', '.'));
    const n2 = parseFloat(raw2.replace(',', '.'));
    if (isNaN(n1) || isNaN(n2)) continue;

    if (validPair(n1, n2)) {
      return mkResult(input, n1, n2, 'european_glued', 'high',
        `Coma europea pegada: "${input}" → X=${n1}, Y=${n2}`);
    }
    if (validPair(n2, n1)) {
      return mkResult(input, n2, n1, 'european_glued', 'high',
        `Coma europea pegada: "${input}" → X=${n2}, Y=${n1}`);
    }
  }
  return null;
}

// ============================================================================
// S3: ENTERO DE 13 DÍGITOS (Y7+X6 ó X6+Y7)
// Patrón estándar: 7 dígitos Y (4_0xx_xxx) + 6 dígitos X (1xx_xxx–6xx_xxx)
// ============================================================================

function tryInteger13(input: string): SplitResult | null {
  if (!/^\d{13}$/.test(input)) return null;

  // Y7 + X6 (más probable: Y empieza por "4")
  const y7 = Number(input.substring(0, 7));
  const x6 = Number(input.substring(7));
  if (validPair(x6, y7)) {
    return mkResult(input, x6, y7, 'Y7X6', 'high',
      `13 dígitos Y7+X6: "${input}" → X=${x6}, Y=${y7}`);
  }

  // X6 + Y7
  const x6b = Number(input.substring(0, 6));
  const y7b = Number(input.substring(6));
  if (validPair(x6b, y7b)) {
    return mkResult(input, x6b, y7b, 'X6Y7', 'high',
      `13 dígitos X6+Y7: "${input}" → X=${x6b}, Y=${y7b}`);
  }

  return null;
}

// ============================================================================
// S4: ENTERO HEURÍSTICO (12 ó 14-15 dígitos)
// Para longitudes no estándar, prueba múltiples puntos de corte.
// ============================================================================

function tryHeuristicInteger(input: string): SplitResult | null {
  if (!/^\d+$/.test(input)) return null;
  const len = input.length;
  if (len < 12 || len > 15 || len === 13) return null; // 13 ya cubierto por S3

  // Probar cortes donde una parte tenga 6-7 dígitos (X o Y)
  for (let cut = 5; cut <= Math.min(8, len - 5); cut++) {
    const a = Number(input.substring(0, cut));
    const b = Number(input.substring(cut));

    if (validPair(a, b)) {
      return mkResult(input, a, b, 'heuristic_split', 'low',
        `${len} dígitos heurístico: "${input}" → X=${a}, Y=${b}`);
    }
    if (validPair(b, a)) {
      return mkResult(input, b, a, 'heuristic_split', 'low',
        `${len} dígitos heurístico: "${input}" → X=${b}, Y=${a}`);
    }
  }

  // Para 15 dígitos: Y7 + X6 + 2 decimales implícitas
  if (len === 15) {
    const y15 = Number(input.substring(0, 7));
    const xInt = Number(input.substring(7, 13));
    const xDec = input.substring(13);
    const xFull = parseFloat(`${xInt}.${xDec}`);
    if (validPair(xFull, y15)) {
      return mkResult(input, xFull, y15, 'heuristic_split', 'low',
        `15 dígitos Y7+X6.xx: "${input}" → X=${xFull}, Y=${y15}`);
    }
  }

  return null;
}

// ============================================================================
// S5: COMA COMO SEPARADOR DE COORDENADAS (no decimal)
// Patrón: "4137618,523456" — una sola coma, ambos lados son enteros válidos
// ============================================================================

function tryCommaSeparator(input: string): SplitResult | null {
  const parts = input.split(',');
  if (parts.length !== 2) return null;

  const a = Number(parts[0].trim());
  const b = Number(parts[1].trim());
  if (isNaN(a) || isNaN(b)) return null;

  // Ambos deben ser enteros (si la coma fuera decimal, el segundo sería < 10)
  if (!Number.isInteger(a) || !Number.isInteger(b)) return null;

  if (validPair(b, a)) {
    return mkResult(input, b, a, 'comma_separated', 'medium',
      `Coma como separador: "${input}" → X=${b}, Y=${a}`);
  }
  if (validPair(a, b)) {
    return mkResult(input, a, b, 'comma_separated', 'medium',
      `Coma como separador: "${input}" → X=${a}, Y=${b}`);
  }

  return null;
}

// ============================================================================
// UTILIDAD
// ============================================================================

function mkResult(
  input: string,
  x: number,
  y: number,
  format: ConcatFormat,
  confidence: SplitConfidence,
  message: string,
): SplitResult {
  return { split: true, originalInput: input, x, y, format, confidence, message };
}

// ============================================================================
// API PÚBLICA
// ============================================================================

/**
 * Cadena de estrategias, en orden de prioridad.
 * La primera que produzca un par válido dentro de Andalucía gana.
 */
const SPLIT_STRATEGIES = [
  tryDecimalGlued,     // S1: dos puntos decimales pegados
  tryEuropeanGlued,    // S2: dos comas decimales europeas pegadas
  tryInteger13,        // S3: 13 dígitos exactos (caso más claro)
  tryHeuristicInteger, // S4: 12/14/15 dígitos con heurística
  tryCommaSeparator,   // S5: coma como separador (última: más ambigua)
] as const;

/**
 * Intenta separar una cadena que contenga dos coordenadas UTM concatenadas.
 *
 * @param input - Cadena sospechosa de contener X e Y pegadas
 * @returns Resultado con X/Y separadas, o null si no se detecta concatenación
 */
export function splitConcatenatedCoords(input: string): SplitResult | null {
  const trimmed = input.trim();
  if (!trimmed || trimmed.length < MIN_INPUT_LENGTH) return null;

  for (const strategy of SPLIT_STRATEGIES) {
    const result = strategy(trimmed);
    if (result !== null) return result;
  }

  return null;
}

/**
 * Detección rápida: ¿parece una cadena con coordenadas concatenadas?
 */
export function isConcatenatedCoord(input: string): boolean {
  return splitConcatenatedCoords(input) !== null;
}
